import React, { useEffect, useState } from "react";
import BaseTextField from "../../../components/ui/BaseTextField";
import BaseButton from "../../../components/ui/BaseButton";
import * as yup from "yup";
import { useFormik } from "formik";
import { useMutation } from "@tanstack/react-query";
import { adminUpdateUserProfile, updateUserDetails } from "../../../services/api/user";
import { useDispatch } from "react-redux";
import { showToast } from "../../../store/toast/toastSlice";
import BaseCheckbox from "../../../components/ui/BaseCheckbox";
import useAuth from "../../../hooks/useAuth";
import { UserType } from "../../../utils/enum";
import { useParams } from "react-router-dom";

const validationSchema = yup.object({
  isAustralian: yup.boolean().required("its required"),
  australianBusinessNumber: yup.string("Enter your Business Number").notRequired(),
  gstPercentage: yup.number("Enter your GST percentage").notRequired(),
});

const TaxInformation = ({
  taxInformation,
  gstPercentage,
  foreignContractor,
  reFetchUser,
  close,
  mode,
  onDirtyChange,
}) => {
  const dispatch = useDispatch();
  const { authRole } = useAuth();
  const { id } = useParams();
  const [isEditMode, setIsEditMode] = useState(false);
  const {
    isPending: isUpdateUserDetailsLoading,
    error: isUpdateUserDetailsError,
    data: updatedUserDetails,
    mutateAsync: updateUserDetailsMutation,
  } = useMutation({
    mutationFn: updateUserDetails,
    enabled: false,
  });
  const { mutateAsync: adminUpdateUserProfileMutation, isPending } = useMutation({
    mutationFn: adminUpdateUserProfile,
  });
  const hasTaxData = taxInformation || gstPercentage || foreignContractor;
  const formik = useFormik({
    initialValues: {
      isAustralian: taxInformation ? true : false,
      isForeign: foreignContractor ? true : false,
      australianBusinessNumber: taxInformation,
      gstPercentage: gstPercentage,
    },
    validationSchema: validationSchema,
    onSubmit: (values) => {
      handleUpdateUserDetails();
    },
    validateOnChange: true,
    validateOnBlur: true,
    enableReinitialize: true,
  });
  const buildPayload = (values) => {
    const { isAustralian, isForeign, ...rest } = values;
    const mapped = { ...rest, isForeignContractor: !!isForeign };
    return Object.fromEntries(
      Object.entries(mapped)
        .map(([k, v]) => {
          if (typeof v === "string") v = v.trim();
          if (k === "gstPercentage") v = Number(v);
          return [k, v];
        })
        .filter(([, v]) => v !== "" && v !== undefined),
    );
  };
  useEffect(() => {
    if (hasTaxData) {
      setIsEditMode(false);
    } else {
      setIsEditMode(true);
    }
  }, [taxInformation, gstPercentage, foreignContractor]);

  // Unsaved edits: the Australian toggle, ABN or GST differs from what's saved.
  // Computed explicitly (not via formik.dirty) so it reliably flips the moment the
  // ABN is typed, even though the saved value starts as undefined.
  const isDirty =
    formik.values.isAustralian !== !!taxInformation ||
    formik.values.isForeign !== !!foreignContractor ||
    String(formik.values.australianBusinessNumber || "") !== String(taxInformation || "") ||
    String(formik.values.gstPercentage ?? "") !== String(gstPercentage ?? "");

  // Report unsaved edits up so the side-nav step can prompt the user to Save.
  useEffect(() => {
    onDirtyChange?.(isDirty);
  }, [isDirty]);
  const handleUpdateUserDetails = async () => {
    try {
      authRole === UserType.ADMIN
        ? await adminUpdateUserProfileMutation({
            userId: id,
            data: buildPayload(formik.values),
          })
        : await updateUserDetailsMutation({
            // Foreign contractors have no ABN; clear it so a stale number isn't kept.
            australianBusinessNumber: formik.values.isForeign
              ? null
              : formik.values.australianBusinessNumber,
            isForeignContractor: formik.values.isForeign,
          });
      dispatch(
        showToast({
          message: "Added successfully",
          type: "success",
        }),
      );
      reFetchUser();
      close?.(false);
    } catch (error) {}
  };
  const handleCancel = () => {
    formik.resetForm({
      values: {
        isAustralian: taxInformation ? true : false,
        isForeign: foreignContractor ? true : false,
        australianBusinessNumber: taxInformation || "",
        gstPercentage: gstPercentage || "",
      },
    });

    setIsEditMode(false);
  };
  const isReadOnly = mode !== "USER_SETTINGS" ? !isEditMode : false;

  return (
    <form>
      {mode !== "USER_SETTINGS" && hasTaxData && (
        <div className="flex justify-end gap-2">
          {isEditMode && (
            <BaseButton color="primary" onClick={handleCancel} className="mt-1 py-[5px] px-[38px]">
              Cancel
            </BaseButton>
          )}

          <BaseButton
            color="primary"
            onClick={() => setIsEditMode(true)}
            disabled={isEditMode}
            className={`mt-1 py-[5px] px-[38px] ${
              isEditMode ? "bg-gray-400 text-white cursor-not-allowed" : ""
            }`}
          >
            Edit
          </BaseButton>
        </div>
      )}
      <section className="py-4 px-5">
        {
          <BaseCheckbox
            label={<p className="font-normal text-base">Australian Contractors</p>}
            id="isAustralian"
            name="isAustralian"
            checked={formik.values.isAustralian}
            onChange={() => {
              const next = !formik.values.isAustralian;
              formik.setFieldValue("isAustralian", next);
              // Mutually exclusive with the foreign option.
              if (next) formik.setFieldValue("isForeign", false);
            }}
            onBlur={formik.handleBlur}
            error={formik.touched.isAustralian && Boolean(formik.errors.isAustralian)}
            errorMsg={formik.touched.isAustralian && formik.errors.isAustralian}
            disabled={isReadOnly}
          ></BaseCheckbox>
        }
        <div className="ml-[34px] mb-4">
          <p>
            As per Australian taxation office( ATO)- Any contractor in Australia must have
            registered business to work as contractor. You can be either sole trader, trust or a
            corporate Pty Ltd company. You must provide your ABN ( Australian Business Number) to
            start working on Tech Domain Portal. Tech Domain will be paying contractor under your
            ABN. Contractor will be responsible for their tax or super payment to ATO. Tech Domain
            will not hold/deduct any tax from your payment. 
          </p>
          {formik?.values?.isAustralian && (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-3 lg:gap-x-12 lg:gap-y-4 mt-3">
                <BaseTextField
                  labelShrink={false}
                  label={
                    <p className="text-primary">
                      Australian Business Number <span className="text-red-500">*</span>
                    </p>
                  }
                  optional={false}
                  isDefaultField={false}
                  id="australianBusinessNumber"
                  name="australianBusinessNumber"
                  value={formik.values.australianBusinessNumber}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={
                    formik.touched.australianBusinessNumber &&
                    Boolean(formik.errors.australianBusinessNumber)
                  }
                  helperText={
                    formik.touched.australianBusinessNumber &&
                    formik.errors.australianBusinessNumber
                  }
                  disabled={isReadOnly}
                />
                {authRole === UserType.ADMIN && (
                  <BaseTextField
                    labelShrink={false}
                    label={<p className="text-primary">GST</p>}
                    optional={false}
                    isDefaultField={false}
                    id="gstPercentage"
                    name="gstPercentage"
                    value={formik.values.gstPercentage}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                    error={formik.touched.gstPercentage && Boolean(formik.errors.gstPercentage)}
                    helperText={formik.touched.gstPercentage && formik.errors.gstPercentage}
                  />
                )}
              </div>
            </>
          )}
        </div>

        <BaseCheckbox
          label={<p className="font-normal text-base">Foreign Contractor Acknowledgement</p>}
          id="isForeign"
          name="isForeign"
          checked={formik.values.isForeign}
          onChange={() => {
            const next = !formik.values.isForeign;
            formik.setFieldValue("isForeign", next);
            if (next) {
              formik.setFieldValue("isAustralian", false);
              formik.setFieldValue("australianBusinessNumber", "");
            }
          }}
          onBlur={formik.handleBlur}
          disabled={isReadOnly}
        ></BaseCheckbox>
        <p className="mb-1 ml-[34px]">
          I declare that I am exempt from Australian Tax withholding or ABN requirement because:
        </p>

        <div className="ml-2 ml-12">
          <p>1. I am not an Australian Citizen</p>
          <p>
            2. I will be performing all of services outside of Australia for which I will receive
            payment from Tech Domain. 
          </p>
        </div>
        {!isReadOnly && (
          <div className="flex flex-col items-end gap-1 mt-5">
            {isDirty && (
              <p className="text-amber-600 text-sm font-normal">
                You have unsaved changes — click Save to apply.
              </p>
            )}
            <BaseButton
              loaderSize="22px"
              isLoading={isUpdateUserDetailsLoading}
              color="primary"
              className="py-[5px] px-[38px]"
              type="submit"
              disabled={
                // Disabled until the user actually changes something...
                !isDirty ||
                (!formik.values.isAustralian && !formik.values.isForeign) ||
                (formik.values.isAustralian &&
                  !String(formik.values.australianBusinessNumber || "").trim())
              }
              onClick={formik.handleSubmit}
            >
              Save
            </BaseButton>
          </div>
        )}
      </section>
    </form>
  );
};

export default TaxInformation;
